import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { useSEO, breadcrumb } from "../lib/seo";
import { RatingStars, SoftwareLogo } from "../components/SoftwareCard";

export default function ComparisonDetail() {
  const { slug } = useParams();
  const [comp, setComp] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setComp(null);
    api.get(`/comparisons/${slug}`).then(({ data }) => setComp(data)).catch(() => setNotFound(true));
  }, [slug]);

  useSEO({
    title: comp?.seo_title || comp?.title,
    description: comp?.seo_description || comp?.intro,
    jsonLd: comp && [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Comparisons", path: "/comparisons" },
        { name: comp.title, path: `/compare/${comp.slug}` },
      ]),
      {
        "@type": "Article",
        headline: comp.title,
        description: comp.intro,
        author: { "@type": "Organization", name: "Pool Software Guide" },
        publisher: { "@type": "Organization", name: "Pool Software Guide" },
      },
    ],
  });

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-slate-500">Comparison not found. <Link to="/comparisons" className="text-sky-600 underline">Browse comparisons</Link></div>;
  if (!comp) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-slate-400">Loading…</div>;

  const groups = [];
  (comp.rows || []).forEach((r) => {
    const g = r.group || "General";
    if (!groups.length || groups[groups.length - 1].name !== g) groups.push({ name: g, rows: [] });
    groups[groups.length - 1].rows.push(r);
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16" data-testid="comparison-detail-page">
      <nav className="text-xs text-slate-400 mb-8">
        <Link to="/" className="hover:text-sky-600">Home</Link> / <Link to="/comparisons" className="hover:text-sky-600">Comparisons</Link> / <span className="text-slate-600">{comp.title}</span>
      </nav>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">{comp.title}</h1>
      <p className="mt-4 text-slate-600 max-w-3xl leading-relaxed">{comp.intro}</p>

      {comp.key_differences?.length > 0 && (
        <div className="mt-6 bg-slate-50 border border-border rounded-2xl p-5 sm:p-6" data-testid="key-differences">
          <h2 className="font-heading font-bold text-slate-900 text-xs uppercase tracking-[0.15em] mb-3">Key differences at a glance</h2>
          <ul className="space-y-2.5">
            {comp.key_differences.map((k, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                <span className="text-sky-500 mt-0.5 shrink-0 font-bold">→</span> <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Contenders */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {(comp.software || []).map((s) => (
          <Link key={s.slug} to={`/software/${s.slug}`} data-testid={`contender-card-${s.slug}`} className="group bg-white rounded-2xl border border-border p-6 flex items-center gap-4 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 transition-shadow duration-200">
            <SoftwareLogo software={s} size="w-14 h-14" />
            <div>
              <h2 className="font-heading font-bold text-lg text-slate-900 group-hover:text-sky-600 transition-colors duration-200">{s.name}</h2>
              <RatingStars rating={s.rating} reviewTotal={s.review_total} />
              <p className="text-xs text-slate-500 mt-1">{s.pricing_start}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Table */}
      <div className="mt-12 overflow-x-auto rounded-2xl border border-border bg-white" data-testid="comparison-table">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="sticky top-16 z-10">
            <tr className="bg-slate-900 text-white">
              <th className="text-left px-5 py-4 font-heading font-semibold w-1/3">Criteria</th>
              {(comp.software || []).map((s) => (
                <th key={s.slug} className="text-left px-5 py-4 font-heading font-semibold">{s.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => (
              <GroupRows key={`${g.name}-${gi}`} group={g} colCount={(comp.software || []).length} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom line per contender */}
      {comp.bottom_line && (comp.software || []).length === 2 && (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="bottom-line">
          {[["a", 0], ["b", 1]].map(([key, idx]) => {
            const s = comp.software[idx];
            if (!s) return null;
            return (
              <div key={key} className="bg-white rounded-2xl border border-border p-6">
                <div className="flex items-center gap-3 mb-3">
                  <SoftwareLogo software={s} size="w-10 h-10" />
                  <h3 className="font-heading font-bold text-slate-900">Choose {s.name} if…</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{comp.bottom_line[key]}</p>
                <Link to={`/software/${s.slug}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-sky-600 hover:text-sky-700">
                  Read the {s.name} review →
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* Verdict */}
      <div className="mt-12 bg-sky-50 border border-sky-100 rounded-2xl p-8" data-testid="comparison-verdict">
        <h2 className="font-heading font-bold text-slate-900 text-sm uppercase tracking-[0.15em] mb-3">Our Verdict</h2>
        <div className="prose-pool"><ReactMarkdown remarkPlugins={[remarkGfm]}>{comp.verdict}</ReactMarkdown></div>
      </div>
    </div>
  );
}

function GroupRows({ group, colCount }) {
  return (
    <>
      <tr className="bg-sky-50">
        <td colSpan={colCount + 1} className="px-5 py-2 font-heading font-bold text-xs uppercase tracking-[0.15em] text-sky-700">{group.name}</td>
      </tr>
      {group.rows.map((r, ri) => (
        <tr key={`${r.criterion}-${ri}`} className="border-t border-border">
          <td className="px-5 py-3.5 font-semibold text-slate-800">{r.criterion}</td>
          {r.values.map((v, i) => (
            <td key={i} className="px-5 py-3.5 text-slate-600 leading-relaxed">{v}</td>
          ))}
        </tr>
      ))}
    </>
  );
}
